import React, { useEffect } from 'react';
import { useEventTracking } from '../hooks/useEventTracking';
import {ContactInfo} from "@/components/ContactInfo.tsx";
import { CONTACT_DETAILS } from '../config/contactDetails';
import { Helmet } from 'react-helmet-async';

export const PrivacyPolicyPage: React.FC = () => {
    const { trackPageView } = useEventTracking();

    useEffect(() => {
        trackPageView('/privacy');
    }, [trackPageView]);

    return (
        <>
            <Helmet>
                <title>{`Privacy Policy | ${CONTACT_DETAILS.company.name}`}</title>
                <meta name="description" content={`Privacy Policy for ${CONTACT_DETAILS.company.name}. Learn how we collect, use, and protect your personal information in accordance with PIPA and Canadian privacy laws.`} />
                <meta name="robots" content="noindex, follow" />
                <link rel="canonical" href="https://4castlabs.com/privacy" />
                <meta property="og:title" content={`Privacy Policy | ${CONTACT_DETAILS.company.name}`} />
                <meta property="og:description" content={`Privacy Policy for ${CONTACT_DETAILS.company.name}. Learn how we protect your personal information.`} />
                <meta property="og:url" content="https://4castlabs.com/privacy" />
                <meta property="og:type" content="website" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 py-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-white shadow-sm rounded-lg p-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>

                    <div className="prose prose-lg max-w-none">
                        <p className="text-gray-600 mb-6">
                            <strong>Effective Date:</strong> January 1, 2025
                        </p>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
                            <p className="text-gray-700 mb-4">
                                {CONTACT_DETAILS.company.name} ("we," "our," or "us") respects your privacy and is committed to protecting
                                your personal information in accordance with the Personal Information Protection Act (PIPA) of British Columbia
                                and Alberta, and other applicable Canadian privacy laws. This privacy policy explains how we collect, use,
                                disclose, and protect your personal information when you visit our website or use our predictive maintenance services.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>

                            <h3 className="text-xl font-medium text-gray-900 mb-3">2.1 Personal Information You Provide</h3>
                            <p className="text-gray-700 mb-2">We collect personal information that you voluntarily provide to us, including:</p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                                <li>Contact information (name, email address, phone number, mailing address)</li>
                                <li>Company information, job title, and business contact details</li>
                                <li>Assessment responses, equipment data, and maintenance requirements</li>
                                <li>Communication preferences and consent records</li>
                                <li>Billing and payment information (when applicable)</li>
                                <li>Any other information you voluntarily provide in forms or communications</li>
                            </ul>

                            <h3 className="text-xl font-medium text-gray-900 mb-3">2.2 Information We Collect Automatically</h3>
                            <p className="text-gray-700 mb-2">When you visit our website, we automatically collect certain technical information:</p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                                <li>Website usage data, analytics, and interaction patterns</li>
                                <li>IP address, general location information (city/province level)</li>
                                <li>Device information (browser type, operating system, device type)</li>
                                <li>Pages visited, time spent on website, and navigation paths</li>
                                <li>Referral sources and search terms used to find our website</li>
                                <li>Session information and unique session identifiers</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Purposes for Collection and Use</h2>
                            <p className="text-gray-700 mb-4">
                                We collect and use your personal information for the following purposes, which we have identified as reasonable:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                                <li>To provide and deliver our predictive maintenance services and solutions</li>
                                <li>To respond to your inquiries and provide customer support</li>
                                <li>To process and send you assessment results and maintenance recommendations</li>
                                <li>To communicate about our services, industry insights, and relevant updates (with consent)</li>
                                <li>To improve our website performance, user experience, and service offerings</li>
                                <li>To process payments and maintain billing records</li>
                                <li>To comply with legal obligations and protect our legitimate business interests</li>
                                <li>To prevent fraud and ensure the security of our systems</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Consent and Legal Basis</h2>
                            <p className="text-gray-700 mb-4">
                                We obtain your consent before collecting, using, or disclosing your personal information, except where
                                permitted or required by law. You may provide consent:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                                <li>Expressly (such as when you complete forms or opt-in to communications)</li>
                                <li>Impliedly (such as when you provide information for an obvious purpose)</li>
                                <li>Through your use of our website and services</li>
                            </ul>
                            <p className="text-gray-700 mb-4">
                                You can withdraw your consent at any time, subject to legal or contractual restrictions.
                                Withdrawal may limit our ability to provide certain services.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Disclosure of Personal Information</h2>
                            <p className="text-gray-700 mb-4">
                                We do not sell your personal information. We may disclose your personal information in the following circumstances:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                                <li>To service providers who assist in operating our website and delivering our services,
                                    under strict confidentiality agreements</li>
                                <li>When required or permitted by law, court order, or government authority</li>
                                <li>To protect our rights, property, or safety, or that of others</li>
                                <li>In connection with a business transaction (merger, acquisition, sale of assets)</li>
                                <li>With your explicit consent for specific purposes</li>
                                <li>To professional advisors (lawyers, accountants) under professional privilege</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Security and Safeguards</h2>
                            <p className="text-gray-700 mb-4">
                                We implement appropriate administrative, technical, and physical safeguards to protect your
                                personal information against unauthorized access, disclosure, copying, use, or modification:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                                <li>Encryption of personal information in transit and at rest</li>
                                <li>Regular security assessments, monitoring, and updates</li>
                                <li>Access controls, authentication measures, and role-based permissions</li>
                                <li>Employee training on privacy and data protection practices</li>
                                <li>Secure disposal of personal information when no longer needed</li>
                                <li>Incident response procedures for potential data breaches</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Privacy Rights</h2>
                            <p className="text-gray-700 mb-4">Under PIPA and other applicable laws, you have the following rights:</p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                                <li><strong>Access:</strong> Request access to your personal information in our custody or control</li>
                                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete personal information</li>
                                <li><strong>Withdrawal of Consent:</strong> Withdraw consent for future collection, use, or disclosure</li>
                                <li><strong>Complaint:</strong> File a complaint with us or the Privacy Commissioner if you believe
                                    your privacy rights have been violated</li>
                                <li><strong>Information:</strong> Receive information about our privacy practices and policies</li>
                            </ul>
                            <p className="text-gray-700 mb-4">
                                To exercise these rights, please contact us using the information provided in Section 12.
                                We will respond to your requests within 30 days as required by law.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Cookies and Tracking Technologies</h2>
                            <p className="text-gray-700 mb-4">
                                We use cookies and similar tracking technologies to enhance your browsing experience,
                                analyze website performance, and understand user behavior. For detailed information
                                about our use of cookies, please see our
                                <a href="/cookies" className="text-primary-600 hover:text-primary-700 underline ml-1">
                                    Cookie Policy
                                </a>.
                            </p>
                            <p className="text-gray-700 mb-4">
                                You can control cookie preferences through your browser settings or our cookie banner.
                                Please note that disabling certain cookies may affect website functionality.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Retention of Personal Information</h2>
                            <p className="text-gray-700 mb-4">
                                We retain your personal information only as long as necessary to fulfill the identified purposes
                                or as required by applicable law. Our general retention periods are:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                                <li>Assessment data and recommendations: 7 years for business and legal purposes</li>
                                <li>Communication records and correspondence: 7 years</li>
                                <li>Website analytics and usage data: 2 years</li>
                                <li>Marketing consent records: Until consent is withdrawn plus 1 year</li>
                                <li>Billing and payment records: As required by Canadian tax and business laws</li>
                            </ul>
                            <p className="text-gray-700 mb-4">
                                Personal information is securely destroyed or anonymized when no longer needed.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Cross-Border Transfers</h2>
                            <p className="text-gray-700 mb-4">
                                Your personal information may be transferred to and processed by service providers located
                                outside of Canada, including in the United States. When we transfer personal information
                                outside Canada, we ensure appropriate contractual and technical safeguards are in place
                                to protect your information in accordance with Canadian privacy laws.
                            </p>
                            <p className="text-gray-700 mb-4">
                                Please note that personal information in foreign jurisdictions may be subject to disclosure
                                under the laws of those jurisdictions.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Children's Privacy</h2>
                            <p className="text-gray-700 mb-4">
                                Our services are intended for business use and are not directed to individuals under 18 years of age.
                                We do not knowingly collect personal information from children under 18. If we become aware that
                                we have collected personal information from a child under 18, we will take steps to delete such information.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
                            <p className="text-gray-700 mb-4">
                                We may update this privacy policy from time to time to reflect changes in our practices,
                                technology, legal requirements, or other factors. We will provide notice of material changes
                                by posting the updated policy on our website with a new effective date. We encourage you to
                                review this policy periodically.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Privacy Officer and Contact Information</h2>
                            <p className="text-gray-700 mb-4">
                                If you have any questions about this privacy policy, our privacy practices, or wish to exercise
                                your privacy rights, please contact our Privacy Officer:
                            </p>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <ContactInfo
                                    variant="default"
                                    className="space-y-3"
                                    showPhone={false}
                                    showAddress={false}
                                    emailType='privacy'
                                />
                                <p className="text-sm text-gray-600 mt-3">
                                    Please mark correspondence "Attention: Privacy Officer"
                                </p>
                            </div>
                            <p className="text-gray-700 mt-4">
                                If you are not satisfied with our response to your privacy concerns, you may contact:
                            </p>
                            <div className="bg-gray-50 p-4 rounded-lg mt-2">
                                <p className="text-sm text-gray-700">
                                    <strong>British Columbia:</strong> Office of the Information and Privacy Commissioner<br/>
                                    Website: <a href="https://www.oipc.bc.ca" className="text-primary-600 hover:text-primary-700 underline">www.oipc.bc.ca</a><br/>
                                    Phone: 1-800-663-7867
                                </p>
                                <p className="text-sm text-gray-700 mt-2">
                                    <strong>Alberta:</strong> Office of the Information and Privacy Commissioner<br/>
                                    Website: <a href="https://www.oipc.ab.ca" className="text-primary-600 hover:text-primary-700 underline">www.oipc.ab.ca</a><br/>
                                    Phone: 1-888-878-4044
                                </p>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
            </div>
        </>
    );
};